import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isIncapacityCancelable, type Incapacity } from '@domain/entities/Incapacity';
import { seedColorForSubArea } from '@domain/entities/Organization';
import type { IncapacityStatus } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import {
  Avatar,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  Modal,
  Spinner,
  cssVars,
  cx,
  Pagination,
} from '../../design-system';
import { usePagination } from '../../hooks/usePagination';
import {
  formatDateRange,
  formatNaturalDays,
  incapacityStatusColors,
  MONTHS_SHORT,
} from '../../format';
import { DocumentViewer } from '../../documents/DocumentViewer';
import { nombreDescarga } from '../../documents/nombreDescarga';
import { useAbsenceScope } from '../../hooks/useAbsenceScope';
import { useAccess } from '../../hooks/useAccess';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import shared from '../shared.module.css';
import { IncapacityWizard } from './IncapacityWizard';
import styles from './AbsencesScreens.module.css';
import { loadFailed } from '../../loadError';
import { usePersonLink } from '../../hooks/usePersonLink';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Ausencias por incapacidad — transcribed from
 * `design/reference/screens/ausencias-por-incapacidad.html`.
 *
 * Two things set this screen apart and both are visible in the markup:
 *
 *  - Days are **natural**, not business. A seven-day medical leave covers the
 *    weekend, because the IMSS subsidy is paid per calendar day. A business count
 *    here would understate the absence and misstate payroll.
 *  - These days never touch the vacation balance.
 *
 * The certificate is health data, so the document itself is only ever referenced
 * by storage path and read behind the storage rules.
 */

/* Order and wording transcribed from the prototype's `incFilters`. */
const FILTERS: readonly { id: IncapacityStatus | 'todas'; label: string }[] = [
  { id: 'todas', label: 'Todas' },
  { id: 'Por validar', label: 'Por validar' },
  { id: 'Validada', label: 'Validada' },
  { id: 'Programada', label: 'Programada' },
  { id: 'Observada', label: 'Observada' },
];

export const IncapacitiesScreen = (): ReactNode => {
  const openPerson = usePersonLink();
  const session = useSession();
  const { holds, featureOn } = useAccess();
  const absences = useAbsenceScope();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<IncapacityStatus | 'todas'>('todas');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cancelling, setCancelling] = useState<Incapacity | null>(null);
  /* El certificado que se está viendo. Hasta hoy el botón sólo enseñaba un
     aviso: el archivo llevaba desde el primer día subido y sin forma de abrirlo. */
  const [certificate, setCertificate] = useState<Incapacity | null>(null);

  /*
   * The viewer's own certificates, stated rather than implied. Validating
   * somebody else's is RR.HH.'s job and lives in Aprobaciones — on a screen that
   * also lists the viewer's own leave, the «Validar» button would have sat on it.
   */
  const scopeNote = 'Solo tus registros';
  const canRegister = holds('solicitar');

  const incapacities = useQuery({
    queryKey: ['incapacities', absences.scope],
    queryFn: async () => {
      const result = await getServices().incapacities.list(absences.scope, { limit: 50 });
      if (!result.ok) throw loadFailed('No pudimos cargar las incapacidades.', result.error);
      return result.value.items;
    },
  });

  const all = incapacities.data ?? [];
  const visible = all.filter((i) => filter === 'todas' || i.status === filter);
  /* De diez en diez, como las demás listas de ausencias. */
  const pager = usePagination(visible, filter);

  if (!featureOn('incapacidades')) {
    return (
      <Card title="Ausencias por incapacidad">
        <Banner tone="info">
          El módulo de incapacidades está apagado. Enciéndelo en Features y módulos.
        </Banner>
        {/*
          El aviso de arriba viene del prototipo y manda a la pantalla global, que
          ya no es el único sitio: un área puede encenderlo para sí sola aunque la
          empresa lo tenga apagado. Sin esta segunda línea, quien administra un
          área lee «enciéndelo en Features y módulos», descubre que no tiene ese
          permiso, y se queda sin saber que sí tiene el suyo.
        */}
        <Banner tone="info">
          También puede encenderse sólo para un área, desde Incapacidades y permisos,
          aunque la empresa lo tenga apagado.
        </Banner>
      </Card>
    );
  }


  /*
   * Transcribed from the prototype's `incKpis`.
   *
   * The four answer a validator's questions in order: how much leave the period
   * carries, how much of it is settled, how much needs them today, and how much
   * is a workplace injury — the last one because a ST-7 has a different reporting
   * duty than an ordinary illness, not because it is a bigger number.
   */
  const kpis: readonly {
    label: string;
    value: number | string;
    chip: string;
    note: string;
    color: string;
  }[] = [
    {
      label: 'Días del periodo',
      value: all.reduce((sum, i) => sum + i.naturalDays, 0),
      chip: String(getServices().clock.today().year),
      note: 'días naturales de incapacidad',
      color: 'var(--color-warning)',
    },
    {
      label: 'Casos activos',
      value: all.filter((i) => i.status === 'Validada' || i.status === 'Programada').length,
      chip: 'Activos',
      note: 'validados o programados',
      color: 'var(--color-success)',
    },
    {
      label: 'Por validar',
      value: all.filter((i) => i.status === 'Por validar' || i.status === 'Observada').length,
      chip: 'Atención',
      note: 'esperan documento o revisión',
      color: 'var(--color-danger)',
    },
    {
      label: 'Riesgo de trabajo',
      value: all.filter((i) => i.kind === 'Riesgo de trabajo').length,
      chip: 'IMSS',
      note: 'reportados al IMSS (ST-7)',
      color: 'var(--color-violet)',
    },
  ];


  return (
    <div className="flow">
      <header className={styles.header}>
        <div>
          <PageTitle className={styles.title}>Ausencias por incapacidad</PageTitle>
          <p className={styles.note}>
            {scopeNote} · las incapacidades del IMSS no consumen días de vacaciones.
          </p>
        </div>

        {canRegister && (
          <button
            type="button"
            className={styles.cta}
            style={cssVars({
              '--cta-gradient': 'linear-gradient(135deg, #e8930c, #c87a05)',
              '--cta-shadow': '0 10px 22px -12px rgb(232 147 12 / 90%)',
            })}
            onClick={() => {
              setWizardOpen(true);
            }}
          >
            <span className={styles.ctaPlus} aria-hidden="true">
              +
            </span>{' '}
            Registrar incapacidad
          </button>
        )}
      </header>

      {wizardOpen && (
        <IncapacityWizard
          onClose={() => {
            setWizardOpen(false);
          }}
          onSubmitted={async () => {
            await queryClient.invalidateQueries({ queryKey: ['incapacities'] });
            await queryClient.invalidateQueries({ queryKey: ['home'] });
          }}
        />
      )}

      <div className={styles.kpis}>
        {kpis.map((kpi) => (
          <div key={kpi.label} className={styles.kpi} style={cssVars({ '--kpi-color': kpi.color })}>
            <div className={styles.kpiHead}>
              <span className={styles.kpiLabel}>{kpi.label}</span>
              <Chip background="var(--kpi-tint)" foreground={kpi.color}>
                {kpi.chip}
              </Chip>
            </div>
            <div className={styles.kpiValue}>{kpi.value}</div>
            <div className={styles.kpiNote}>{kpi.note}</div>
          </div>
        ))}
      </div>

      <div
        className={cx(styles.filters, 'scroll-x')}
        role="group"
        aria-label="Filtrar incapacidades"
      >
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={filter === option.id}
            className={cx(styles.filter, filter === option.id && styles.filterActive)}
            onClick={() => {
              setFilter(option.id);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {incapacities.isLoading ? (
        <Card>
          <Spinner label="Cargando" />
        </Card>
      ) : incapacities.error !== null ? (
        <Banner tone="danger">No pudimos cargar las incapacidades.</Banner>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState glyph="✚" title="Sin incapacidades" note="No hay registros con ese filtro." />
        </Card>
      ) : (
        <ul className={styles.incapacityList}>
          {pager.items.map((incapacity) => {
            const colors = incapacityStatusColors(incapacity.status);
            const accent = seedColorForSubArea(incapacity.subAreaName);
            const statusAccent =
              incapacity.status === 'Validada' || incapacity.status === 'Programada'
                ? 'var(--color-success)'
                : incapacity.status === 'Observada'
                  ? 'var(--color-danger-bright)'
                  : 'var(--color-warning)';

            return (
              <li
                key={incapacity.id}
                className={styles.incapacityRow}
                style={cssVars({
                  '--row-accent': accent,
                  '--date-bg': colors.background,
                  '--date-fg': colors.foreground,
                  '--date-accent': statusAccent,
                })}
              >
                <div className={styles.dateBox}>
                  <span className={styles.dateMonth}>
                    {MONTHS_SHORT[incapacity.range.start.month - 1]}
                  </span>
                  <span className={styles.dateDay}>{incapacity.range.start.day}</span>
                </div>

                <div>
                  <div className={styles.incapacityWho}>
                    <Avatar
                      name={incapacity.personName}
                      color={accent}
                      size={34}
                      onOpen={openPerson(incapacity.personId)}
                    />
                    <div className={styles.incapacityWhoText}>
                      <div className={styles.incapacityName}>{incapacity.personName}</div>
                      <div className={styles.incapacitySub}>
                        {incapacity.subAreaName} · {incapacity.folio}
                      </div>
                    </div>
                  </div>

                  <div className={styles.incapacityRange}>
                    {formatDateRange(incapacity.range)}
                  </div>
                  <div className={styles.incapacityDays}>
                    {formatNaturalDays(incapacity.naturalDays)} · {incapacity.kind}
                  </div>
                  {incapacity.notes.length > 0 && (
                    <p className={styles.incapacityDetail}>{incapacity.notes}</p>
                  )}
                  {incapacity.observation !== null && (
                    <p className={shared.rowReason}>{incapacity.observation}</p>
                  )}
                </div>

                <div className={styles.coverageCell}>
                  <span className={styles.cellLabel}>Cobertura</span>
                  <span className={styles.coverageText}>
                    {incapacity.coverage.length > 0 ? incapacity.coverage : 'Sin definir'}
                  </span>
                  <div className={styles.coverageTags}>
                    <Chip
                      background={
                        incapacity.certificatePath !== null
                          ? 'var(--color-success-bg)'
                          : 'var(--color-danger-bg)'
                      }
                      foreground={
                        incapacity.certificatePath !== null
                          ? 'var(--color-success-ink)'
                          : 'var(--color-danger-ink)'
                      }
                    >
                      {incapacity.certificatePath !== null
                        ? 'Certificado IMSS'
                        : 'Falta certificado'}
                    </Chip>
                    <Chip
                      background="var(--color-surface-subtle-alt)"
                      foreground="var(--color-brand-ink)"
                    >
                      {incapacity.subsidy}
                    </Chip>
                  </div>
                  {incapacity.umf.length > 0 && (
                    <span className={styles.coverageText}>UMF {incapacity.umf}</span>
                  )}
                </div>

                <div className={styles.incapacityActions}>
                  <Chip background={colors.background} foreground={colors.foreground}>
                    {incapacity.status}
                  </Chip>


                  <button
                    type="button"
                    className={styles.certificate}
                    disabled={incapacity.certificatePath === null}
                    onClick={() => {
                      setCertificate(incapacity);
                    }}
                  >
                    ↓ Certificado
                  </button>

                  {/* Sólo las propias, y sólo mientras RR.HH. no las haya
                      validado: después es un registro aceptado, no un borrador. */}
                  {incapacity.personId === session.personId &&
                    isIncapacityCancelable(incapacity) && (
                      <button
                        type="button"
                        className={styles.incapacityCancel}
                        onClick={() => {
                          setCancelling(incapacity);
                        }}
                      >
                        Retirar
                      </button>
                    )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination pager={pager} noun="incapacidades" detached />

      {certificate !== null && certificate.certificatePath !== null && (
        <DocumentViewer
          documentos={[
            {
              titulo: `Certificado de ${certificate.folio}`,
              path: certificate.certificatePath,
              nombreArchivo: nombreDescarga('Certificado', certificate.folio, certificate.personName),
            },
          ]}
          subtitulo={certificate.personName}
          onClose={() => {
            setCertificate(null);
          }}
        />
      )}

      {cancelling !== null && (
        <WithdrawDialog
          incapacity={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
          onDone={async () => {
            setCancelling(null);
            await queryClient.invalidateQueries({ queryKey: ['incapacities'] });
            await queryClient.invalidateQueries({ queryKey: ['absences'] });
            await queryClient.invalidateQueries({ queryKey: ['approvals'] });
            await queryClient.invalidateQueries({ queryKey: ['home'] });
            await queryClient.invalidateQueries({ queryKey: ['shell', 'badge'] });
          }}
        />
      )}
    </div>
  );
};

/**
 * Retirar un certificado propio.
 *
 * «Retirar» y no «cancelar»: una incapacidad no se cancela —el médico ya la
 * expidió y los días pasaron igual—, lo que se retira es el registro que se
 * capturó mal. La palabra tiene que decir eso, o la persona creerá que está
 * deshaciendo la incapacidad misma.
 *
 * El motivo es texto libre y no del catálogo, que sí usa la cancelación de
 * vacaciones. Ese catálogo habla de reagendar días —«prefiero mover los días
 * para no dejar al equipo corto»— y ninguna de sus frases dice «subí el
 * certificado equivocado».
 */
const WithdrawDialog = ({
  incapacity,
  onClose,
  onDone,
}: {
  incapacity: Incapacity;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (reason.trim().length === 0) return;
    setBusy(true);

    const result = await getServices().incapacities.cancel(incapacity.id, reason.trim());
    setBusy(false);

    if (!result.ok) {
      toast.bad(
        result.error.kind === 'unavailable'
          ? 'No se pudo retirar. Revisa tu conexión.'
          : 'No se pudo retirar esa incapacidad.',
      );
      return;
    }

    toast.ok(`${incapacity.folio} quedó retirada.`);
    await onDone();
  };

  return (
    <Modal
      title={`Retirar ${incapacity.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.trim().length === 0}
            onClick={() => {
              void submit();
            }}
          >
            Retirar el registro
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          El registro del {formatDateRange(incapacity.range)} deja de contar y RR.HH. verá por
          qué. Si la incapacidad existe pero quedó mal capturada, regístrala de nuevo con los
          datos correctos — el certificado no se borra de ningún expediente.
        </p>

        <Field label="Por qué la retiras">
          {(id) => (
            <textarea
              id={id}
              className={styles.withdrawText}
              rows={3}
              maxLength={500}
              placeholder="Ej. Capturé mal las fechas; la vuelvo a subir."
              value={reason}
              onChange={(event) => {
                setReason(event.target.value.slice(0, 500));
              }}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
};
